import { useMutation } from '@tanstack/react-query';
import { Modal, Form, FormProps, Row, Col, Select, notification, Button } from 'antd';
import useModalStore from 'stores/useModal';

import { ReactComponent as IconClose } from 'assets/images/close.svg';

import QUERY_KEYS from 'services/api/queryKeys';
import { PermissionActionTypes } from 'helper/type/action.type';
import IBButton from 'helper/permission.action';
import { useTranslation } from 'react-i18next';
import { queryClient } from 'index';
import { updateTourStatus } from 'services/api/module/report.api';
import { TOUR_STATUS_OPTIONS } from 'constants/form';

function UpdateTourStatusModal({ tourId }: { tourId: number }) {
  const { t } = useTranslation();

  const tourStatusOptions = TOUR_STATUS_OPTIONS(t);

  const [formTourStatus] = Form.useForm();

  const { isOpenUpdateTourStatus, setIsOpenUpdateTourStatus } = useModalStore();

  const { mutateAsync: mutateUpdateTourStatus, isPending: isPendingUpdate } = useMutation({
    mutationFn: (params: any = {}) => updateTourStatus(tourId, params)
  });

  const handleCloseUpdateTourStatus = () => {
    setIsOpenUpdateTourStatus(false);
    formTourStatus.resetFields();
  };

  const handleUpdateTourStatus = async () => {
    formTourStatus.submit();
  };

  const onFinishUpdateTourStatus: FormProps['onFinish'] = async (values: any) => {
    const { tourStatus } = values;
    try {
      await mutateUpdateTourStatus({
        tour_status: tourStatus
      });

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_TOURS_DATA]
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_TOUR_DETAIL]
      });

      notification.success({
        message: t('tour.statusUpdateSuccess'),
        description: t('tour.statusUpdateSuccessDescription')
      });

      setIsOpenUpdateTourStatus(false);
    } catch (err: any) {
      notification.error({
        message: err.error || t('common.error')
      });
    }
  };

  return (
    <Modal
      title={t('tour.updateTourStatus')}
      centered
      width={410}
      open={isOpenUpdateTourStatus}
      onCancel={handleCloseUpdateTourStatus}
      closeIcon={<IconClose />}
      okText={null}
      cancelText={t('common.actions.cancel')}
      onOk={handleUpdateTourStatus}
      className="modal-update-tour-status"
      confirmLoading={isPendingUpdate}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleCloseUpdateTourStatus}>
          {t('common.actions.cancel')}
        </Button>,
        <IBButton
          key="ok"
          permission={PermissionActionTypes.CHANGE_DATE}
          onClick={handleUpdateTourStatus}
          className="ant-btn-secondary"
        >
          {t('common.actions.save')}
        </IBButton>
      ]}
    >
      <Form
        form={formTourStatus}
        name="update-tour-status-form"
        layout="vertical"
        style={{ width: '100%' }}
        onFinish={onFinishUpdateTourStatus}
        autoComplete="off"
      >
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <Form.Item
              label={t('tour.status')}
              name="tourStatus"
              rules={[{ required: true, message: t('tour.statusRequired') }]}
            >
              <Select options={tourStatusOptions} placeholder={t('tour.selectStatus')} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default UpdateTourStatusModal;
